
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import './work-order.css';
import { apiRequest, buildApiUrl } from '../../../api/client';

interface ClientInfo {
    clientId?: string;
    firstName: string;
    lastName: string;
    buildingName?: string;
    propertyAddress: string;
    phone: string;
    email: string;
}

interface ExistingClient {
    _id: string;
    firstName?: string;
    lastName?: string;
    buildingName?: string;
    address?: string;
    phone?: string;
    email?: string;
}

interface LineItem {
    id: string;
    item: string;
    description: string;
    quantity: number;
    unitPrice: number;
}

interface Document {
    name: string;
    url: string;
    type: string;
    size: number;
    uploadedAt: string;
}

export default function WorkOrderForm(): React.JSX.Element {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sourceQuoteId = searchParams.get('sourceQuoteId') || '';
    const [currentWorkOrderId, setCurrentWorkOrderId] = useState<string | null>(null);
    const [, setCurrentStatus] = useState<'draft' | 'scheduled'>('draft');

    const [clientInfo, setClientInfo] = useState<ClientInfo>({
        clientId: '',
        firstName: '',
        lastName: '',
        buildingName: '',
        propertyAddress: '',
        phone: '',
        email: ''
    });

    const [jobTitle, setJobTitle] = useState('');
    const [jobType, setJobType] = useState<'inspection' | 'troubleshooting' | 'service_call' | 'other'>('other');
    const [instructions, setInstructions] = useState('');
    const [technician, setTechnician] = useState('');
    const [technicians, setTechnicians] = useState<Array<{ _id: string; name: string; email: string }>>([]);
    const [showClientPicker, setShowClientPicker] = useState(false);
    const [clientSearch, setClientSearch] = useState('');
    const [existingClients, setExistingClients] = useState<ExistingClient[]>([]);
    const [isClientSearchLoading, setIsClientSearchLoading] = useState(false);
    const [inspectionTypes, setInspectionTypes] = useState<Array<{ _id: string; frequency?: string; code?: string; value?: string }>>([]);
    const [inspectionFrequency, setInspectionFrequency] = useState('');
    const [inspectionStandardCode, setInspectionStandardCode] = useState<'ULC_S536'>('ULC_S536');
    const [technicianCertNumber, setTechnicianCertNumber] = useState('');
    const [inspectionTimeOnSite, setInspectionTimeOnSite] = useState('');

    const [lineItems, setLineItems] = useState<LineItem[]>([]);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [uploadingFile, setUploadingFile] = useState(false);

    const [scheduleLater, setScheduleLater] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [startTime, setStartTime] = useState('10:00');
    const [endTime, setEndTime] = useState('12:00');
    const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
    const [formTemplateId, setFormTemplateId] = useState('');
    const [formTemplates, setFormTemplates] = useState<Array<{ _id: string; name: string }>>([]);
    const [existingInspectionId, setExistingInspectionId] = useState<string | null>(null);

    // Fetch technicians on mount
    useEffect(() => {
        apiRequest<{ data: Array<{ _id: string; name: string; email: string }> }>('/api/users?role=technician')
            .then((response) => {
                setTechnicians(response.data);
            })
            .catch((error) => {
                console.error('Failed to fetch technicians:', error);
            });
        apiRequest<{ data: Array<{ _id: string; frequency?: string; code?: string; value?: string }> }>('/api/inspections/types')
            .then((response) => setInspectionTypes(Array.isArray(response.data) ? response.data : []))
            .catch(() => setInspectionTypes([]));
        apiRequest<{ success: boolean; data: Array<{ _id: string; name: string }> }>('/api/work-order-form-templates')
            .then((response) => setFormTemplates(Array.isArray(response.data) ? response.data : []))
            .catch(() => setFormTemplates([]));
    }, []);

    useEffect(() => {
        if (!showClientPicker) return;
        const query = clientSearch.trim();
        const timer = window.setTimeout(() => {
            setIsClientSearchLoading(true);
            apiRequest<{ data: ExistingClient[] }>(`/api/clients?q=${encodeURIComponent(query)}&limit=50`)
                .then((res) => setExistingClients(Array.isArray(res.data) ? res.data : []))
                .catch(() => setExistingClients([]))
                .finally(() => setIsClientSearchLoading(false));
        }, 250);
        return () => window.clearTimeout(timer);
    }, [showClientPicker, clientSearch]);

    // Load existing job if editing
    useEffect(() => {
        const preselectedJobType = searchParams.get('jobType');
        if (
            preselectedJobType === 'inspection' ||
            preselectedJobType === 'troubleshooting' ||
            preselectedJobType === 'service_call' ||
            preselectedJobType === 'other'
        ) {
            setJobType(preselectedJobType);
        }

        const workOrderId = searchParams.get('id');
        if (!workOrderId) return;

        apiRequest<{ data: any; inspection?: { _id?: string } | null }>(`/api/work-orders/${workOrderId}`)
            .then((data) => {
                const existing = data.data;
                setCurrentWorkOrderId(existing._id);
                const linkedInspectionId =
                    existing.inspectionId
                    || data.inspection?._id
                    || null;
                setExistingInspectionId(linkedInspectionId ? String(linkedInspectionId) : null);
                setClientInfo({
                    clientId: existing.clientId || existing.client?._id || '',
                    firstName: existing.client?.name?.split(' ')[0] || '',
                    lastName: existing.client?.name?.split(' ').slice(1).join(' ') || '',
                    buildingName: existing.client?.buildingName || '',
                    propertyAddress: existing.client?.address || '',
                    phone: existing.client?.phone || '',
                    email: existing.client?.email || ''
                });
                setJobTitle(existing.title || '');
                setJobType(existing.jobType || 'other');
                setInstructions(existing.instructions || '');
                setTechnician(existing.assignedTechId || existing.technicianId || '');
                setInspectionFrequency(existing.inspectionFrequency || existing.meta?.inspection?.frequency || '');
                setInspectionStandardCode(existing.inspectionStandardCode || existing.meta?.inspection?.standardCode || 'ULC_S536');
                setTechnicianCertNumber(existing.meta?.inspection?.technicianCertNumber || '');
                setInspectionTimeOnSite(existing.meta?.inspection?.timeOnSite || '');
                setLineItems(existing.lineItems || []);
                setDocuments(existing.documents || []);
                setCurrentStatus(existing.status || 'draft');
                if (existing.schedule?.date) {
                    const parsedDate = new Date(existing.schedule.date);
                    if (!Number.isNaN(parsedDate.getTime())) {
                        setSelectedDate(parsedDate);
                        setCurrentMonth(parsedDate);
                    }
                }
                if (existing.schedule?.endDate) {
                    const parsedEndDate = new Date(existing.schedule.endDate);
                    if (!Number.isNaN(parsedEndDate.getTime())) {
                        setSelectedEndDate(parsedEndDate);
                    }
                }
                setStartTime(existing.schedule?.startTime || '10:00');
                setEndTime(existing.schedule?.endTime || '12:00');
                setScheduleLater(!existing.schedule?.date);
                setPriority(existing.priority || 'medium');
                setFormTemplateId(existing.formTemplateId || '');
            })
            .catch(() => {
                setCurrentWorkOrderId(null);
            });
    }, [searchParams]);

    const handleSelectExistingClient = (selected: ExistingClient) => {
        setClientInfo({
            clientId: selected._id,
            firstName: selected.firstName || '',
            lastName: selected.lastName || '',
            buildingName: selected.buildingName || '',
            propertyAddress: selected.address || '',
            phone: selected.phone || '',
            email: selected.email || ''
        });
        setShowClientPicker(false);
        setClientSearch('');
    };

    // Calendar helpers
    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        return { daysInMonth, startingDayOfWeek };
    };

    const formatMonthYear = (date: Date) => {
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    const previousMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    const nextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    const selectDate = (day: number) => {
        const selected = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        if (!selectedDate || (selectedDate && selectedEndDate)) {
            setSelectedDate(selected);
            setSelectedEndDate(null);
            return;
        }

        if (selectedDate && !selectedEndDate) {
            if (selected >= selectedDate) {
                setSelectedEndDate(selected);
            } else {
                alert('End date must be after the start date.');
            }
        }
    };

    const isDateSelected = (day: number) => {
        if (!selectedDate) return false;
        return selectedDate.getDate() === day &&
            selectedDate.getMonth() === currentMonth.getMonth() &&
            selectedDate.getFullYear() === currentMonth.getFullYear();
    };

    const isToday = (day: number) => {
        const today = new Date();
        return today.getDate() === day &&
            today.getMonth() === currentMonth.getMonth() &&
            today.getFullYear() === currentMonth.getFullYear();
    };

    const isInRange = (day: number) => {
        if (!selectedDate || !selectedEndDate) return false;
        const start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
        const end = new Date(selectedEndDate.getFullYear(), selectedEndDate.getMonth(), selectedEndDate.getDate());
        if (end < start) return false;
        const current = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        return current > start && current < end;
    };

    // Handlers
    const handleAddLineItem = () => {
        const newItem: LineItem = {
            id: Date.now().toString(),
            item: '',
            description: '',
            quantity: 1,
            unitPrice: 0
        };
        setLineItems([...lineItems, newItem]);
    };

    const handleRemoveLineItem = (id: string) => {
        setLineItems(lineItems.filter(item => item.id !== id));
    };

    const handleLineItemChange = (id: string, field: keyof LineItem, value: any) => {
        setLineItems(lineItems.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    const calculateSubtotal = () => {
        return lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setUploadingFile(true);
        try {
            const file = files[0];
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(buildApiUrl('/api/upload'), {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            const result = await response.json();
            if (result.success) {
                setDocuments([...documents, result.data]);
                // Reset file input
                event.target.value = '';
            } else {
                alert(result.error || 'Failed to upload file');
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('Failed to upload file');
        } finally {
            setUploadingFile(false);
        }
    };

    const handleRemoveDocument = (index: number) => {
        setDocuments(documents.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const handleSave = async (status: 'draft' | 'scheduled') => {
        if (status === 'scheduled' && !selectedDate) {
            alert('Please choose a schedule date before publishing.');
            return;
        }
        if (!technician) {
            alert('Please select a technician.');
            return;
        }
        if (jobType === 'inspection' && status === 'scheduled') {
            if (!clientInfo.clientId || !clientInfo.propertyAddress || !inspectionFrequency || !inspectionTimeOnSite) {
            alert('For inspection jobs, please select a client, site address, inspection type, and time on site.');
                return;
            }
        }

        const selectedTech = technicians.find((tech) => tech._id === technician);
        const payload = {
            title: jobTitle,
            jobType,
            instructions,
            status,
            assignedTechId: technician,
            technicianName: selectedTech?.name || '',
            technicianEmail: selectedTech?.email || '',
            ...(sourceQuoteId && !currentWorkOrderId ? { sourceQuoteId } : {}),
            clientId: clientInfo.clientId || undefined,
            client: {
                name: `${clientInfo.firstName} ${clientInfo.lastName}`.trim(),
                email: clientInfo.email,
                phone: clientInfo.phone,
                address: clientInfo.propertyAddress
            },
            lineItems,
            documents,
            priority,
            formTemplateId: formTemplateId || undefined,
            inspectionFrequency: jobType === 'inspection' ? inspectionFrequency || undefined : undefined,
            inspectionStandardCode: jobType === 'inspection' ? inspectionStandardCode : undefined,
            meta: {
                inspection: jobType === 'inspection' ? {
                    frequency: inspectionFrequency,
                    standardCode: inspectionStandardCode,
                    technicianCertNumber,
                    timeOnSite: inspectionTimeOnSite
                } : undefined
            },
            schedule: scheduleLater ? {} : {
                date: selectedDate?.toISOString(),
                endDate: selectedEndDate?.toISOString(),
                startTime,
                endTime
            }
        };

        try {
            let savedWorkOrderId = currentWorkOrderId;
            if (currentWorkOrderId) {
                await apiRequest(`/api/work-orders/${currentWorkOrderId}`, {
                    method: 'PATCH',
                    json: payload
                });
            } else {
                const created = await apiRequest<{ data: any }>('/api/work-orders', {
                    method: 'POST',
                    json: payload
                });
                savedWorkOrderId = created.data._id;
                setCurrentWorkOrderId(savedWorkOrderId);
            }

            if (jobType === 'inspection' && status === 'scheduled') {
                if (!existingInspectionId) {
                    const inspection = await apiRequest<{ data: { _id: string } }>('/api/inspections', {
                        method: 'POST',
                        json: {
                            clientId: clientInfo.clientId,
                            siteAddress: clientInfo.propertyAddress,
                            technicianId: technician,
                            frequency: inspectionFrequency,
                            standardCode: inspectionStandardCode,
                            technicianCertNumber,
                            timeOnSite: inspectionTimeOnSite,
                            workOrderId: savedWorkOrderId
                        }
                    });
                    navigate(`/admin/inspections/${inspection.data._id}/field`);
                    return;
                }
            }

            setCurrentStatus(status);
            alert(status === 'draft' ? 'Job saved as draft!' : 'Job published!');
            navigate('/admin/work-orders');
        } catch {
            alert('Failed to save job.');
        }
    };

    const fmtCurrency = (val: number) => `$${val.toFixed(2)}`;

    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
    const editingJobId = searchParams.get('id');

    return (
        <AppShell>
            <div className="wo-page">
                <div className="wo-page-container">
                    {/* Premium Header */}
                    <header className="wo-premium-header">
                        <button className="wo-back-link" onClick={() => navigate('/admin/work-orders')}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Back to Jobs
                        </button>
                        <div className="wo-header-content">
                            <div className="wo-header-badge">{editingJobId ? 'Edit Job' : 'New Job'}</div>
                            <h1 className="wo-main-title">{editingJobId ? 'Edit Job' : 'Create Job'}</h1>
                            <p className="wo-subtitle">
                                {editingJobId
                                    ? 'Update the details below for this job'
                                    : 'Fill in the details below to schedule and assign a new job'}
                            </p>
                        </div>
                    </header>

                    {/* Main Content Grid */}
                    <div className="wo-content-grid">
                        {/* Left Column */}
                        <div className="wo-left-column">
                            {/* Client Info Section */}
                            <section className="wo-card wo-card-animated">
                                <div className="wo-card-header">
                                    <div className="wo-icon-wrapper wo-icon-client">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="wo-card-title">Client Information</h2>
                                        <p className="wo-card-subtitle">Enter the client's contact details</p>
                                    </div>
                                </div>

                                <button
                                    className="wo-add-item-btn"
                                    type="button"
                                    style={{ marginBottom: '16px' }}
                                    onClick={() => setShowClientPicker((prev) => !prev)}
                                >
                                    {showClientPicker ? 'Hide Existing Clients' : 'Select Existing Client'}
                                </button>
                                {showClientPicker && (
                                    <div style={{ border: '1px solid var(--wo-border)', borderRadius: '12px', padding: '12px', marginBottom: '16px', background: '#fff' }}>
                                        <input
                                            className="wo-input"
                                            placeholder="Search by client or address"
                                            value={clientSearch}
                                            onChange={(e) => setClientSearch(e.target.value)}
                                        />
                                        <div style={{ marginTop: '10px', maxHeight: '220px', overflowY: 'auto', display: 'grid', gap: '8px' }}>
                                            {isClientSearchLoading && <div style={{ color: 'var(--wo-text-muted)', fontSize: '0.9rem' }}>Searching clients...</div>}
                                            {!isClientSearchLoading && existingClients.length === 0 && (
                                                <div style={{ color: 'var(--wo-text-muted)', fontSize: '0.9rem' }}>No matching clients found.</div>
                                            )}
                                            {!isClientSearchLoading && existingClients.map((c) => (
                                                <button
                                                    key={c._id}
                                                    type="button"
                                                    className="wo-btn wo-btn-secondary"
                                                    style={{ textAlign: 'left', justifyContent: 'flex-start', padding: '10px 12px' }}
                                                    onClick={() => handleSelectExistingClient(c)}
                                                >
                                                    {(c.firstName || c.lastName) ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : 'Client'}
                                                    {' • '}
                                                    {c.buildingName || c.address || 'No address'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="wo-form-grid">
                                    <div className="wo-form-group">
                                        <label className="wo-label">First Name</label>
                                        <input
                                            type="text"
                                            className="wo-input"
                                            placeholder="John"
                                            value={clientInfo.firstName}
                                            onChange={(e) => setClientInfo({ ...clientInfo, firstName: e.target.value })}
                                        />
                                    </div>

                                    <div className="wo-form-group">
                                        <label className="wo-label">Last Name</label>
                                        <input
                                            type="text"
                                            className="wo-input"
                                            placeholder="Doe"
                                            value={clientInfo.lastName}
                                            onChange={(e) => setClientInfo({ ...clientInfo, lastName: e.target.value })}
                                        />
                                    </div>

                                    <div className="wo-form-group wo-full-width">
                                        <label className="wo-label">Property Address</label>
                                        <input
                                            type="text"
                                            className="wo-input"
                                            placeholder="123 Main Street, City, State 12345"
                                            value={clientInfo.propertyAddress}
                                            onChange={(e) => setClientInfo({ ...clientInfo, propertyAddress: e.target.value })}
                                        />
                                    </div>

                                    <div className="wo-form-group">
                                        <label className="wo-label">Phone Number</label>
                                        <input
                                            type="tel"
                                            className="wo-input"
                                            placeholder="(555) 123-4567"
                                            value={clientInfo.phone}
                                            onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                                        />
                                    </div>

                                    <div className="wo-form-group">
                                        <label className="wo-label">Email Address</label>
                                        <input
                                            type="email"
                                            className="wo-input"
                                            placeholder="client@example.com"
                                            value={clientInfo.email}
                                            onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* Job Overview Section */}
                            <section className="wo-card wo-card-animated">
                                <div className="wo-card-header">
                                    <div className="wo-icon-wrapper wo-icon-job">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="wo-card-title">Job Overview</h2>
                                        <p className="wo-card-subtitle">Describe the work to be performed</p>
                                    </div>
                                </div>

                                <div className="wo-form-grid">
                                    <div className="wo-form-group wo-full-width">
                                        <label className="wo-label">Job Type</label>
                                        <select
                                            className="wo-select"
                                            value={jobType}
                                            onChange={(e) => setJobType(e.target.value as 'inspection' | 'troubleshooting' | 'service_call' | 'other')}
                                        >
                                            <option value="inspection">Inspection</option>
                                            <option value="troubleshooting">Troubleshooting</option>
                                            <option value="service_call">Service Call</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div className="wo-form-group wo-full-width">
                                        <label className="wo-label">Job Title</label>
                                        <input
                                            type="text"
                                            className="wo-input"
                                            placeholder="e.g., Fire Alarm System Installation"
                                            value={jobTitle}
                                            onChange={(e) => setJobTitle(e.target.value)}
                                        />
                                    </div>

                                    <div className="wo-form-group wo-full-width">
                                        <label className="wo-label">Technician</label>
                                        <select
                                            className="wo-select"
                                            value={technician}
                                            onChange={(e) => setTechnician(e.target.value)}
                                        >
                                            <option value="">Select a technician</option>
                                            {technicians.map((tech) => (
                                                <option key={tech._id} value={tech._id}>
                                                    {tech.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="wo-form-group wo-full-width">
                                        <label className="wo-label">Work order form template</label>
                                        <select
                                            className="wo-select"
                                            value={formTemplateId}
                                            onChange={(e) => setFormTemplateId(e.target.value)}
                                        >
                                            <option value="">No daily work order form</option>
                                            {formTemplates.map((tpl) => (
                                                <option key={tpl._id} value={tpl._id}>{tpl.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="wo-form-group wo-full-width">
                                        <label className="wo-label">Instructions</label>
                                        <textarea
                                            className="wo-textarea"
                                            placeholder="Enter detailed instructions for the technician..."
                                            value={instructions}
                                            onChange={(e) => setInstructions(e.target.value)}
                                            rows={4}
                                        />
                                    </div>
                                </div>
                            </section>

                            {jobType === 'inspection' && (
                                <section className="wo-card wo-card-animated">
                                    <div className="wo-card-header">
                                        <div className="wo-icon-wrapper wo-icon-schedule">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <path d="M9 5H7a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                        <div>
                                            <h2 className="wo-card-title">Inspection Setup</h2>
                                            <p className="wo-card-subtitle">These fields power inspection checklist workflow.</p>
                                        </div>
                                    </div>
                                    <div className="wo-form-grid">
                                        <div className="wo-form-group">
                                            <label className="wo-label">Inspection Type</label>
                                            <select className="wo-select" value={inspectionFrequency} onChange={(e) => setInspectionFrequency(e.target.value)}>
                                                <option value="">Select inspection type</option>
                                                {inspectionTypes.map((t) => {
                                                    const value = t.frequency || t.code || t.value || '';
                                                    if (!value) return null;
                                                    return <option key={t._id} value={value}>{value.replace('_', '-').replace(/\b\w/g, (c) => c.toUpperCase())}</option>;
                                                })}
                                            </select>
                                        </div>
                                        <div className="wo-form-group">
                                            <label className="wo-label">Applicable Standard</label>
                                            <div style={{ display: 'flex', gap: '18px', alignItems: 'center', minHeight: '44px' }}>
                                                <span>CAN/ULC-S536:2019</span>
                                            </div>
                                        </div>
                                        <div className="wo-form-group">
                                            <label className="wo-label">Technician Certification Number</label>
                                            <input className="wo-input" value={technicianCertNumber} onChange={(e) => setTechnicianCertNumber(e.target.value)} />
                                        </div>
                                        <div className="wo-form-group">
                                            <label className="wo-label">Time On Site</label>
                                            <input className="wo-input" type="datetime-local" value={inspectionTimeOnSite} onChange={(e) => setInspectionTimeOnSite(e.target.value)} />
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Products/Services Section */}
                            <section className="wo-card wo-card-animated">
                                <div className="wo-card-header">
                                    <div className="wo-icon-wrapper wo-icon-products">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M3.27002 6.96L12 12.01L20.73 6.96" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M12 22.08V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="wo-card-title">Products & Services</h2>
                                        <p className="wo-card-subtitle">Add items to this job</p>
                                    </div>
                                </div>

                                {lineItems.length > 0 && (
                                    <div className="wo-line-items">
                                        {lineItems.map((item, index) => (
                                            <div key={item.id} className="wo-line-item">
                                                <div className="wo-line-item-header">
                                                    <span className="wo-line-item-number">#{index + 1}</span>
                                                    <button
                                                        className="wo-remove-btn"
                                                        onClick={() => handleRemoveLineItem(item.id)}
                                                        title="Remove item"
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                        </svg>
                                                    </button>
                                                </div>
                                                <div className="wo-line-item-grid">
                                                    <div className="wo-form-group wo-full-width">
                                                        <label className="wo-label-sm">Item Name</label>
                                                        <input
                                                            type="text"
                                                            className="wo-input-sm"
                                                            placeholder="Product or service name"
                                                            value={item.item}
                                                            onChange={(e) => handleLineItemChange(item.id, 'item', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="wo-form-group wo-full-width">
                                                        <label className="wo-label-sm">Description</label>
                                                        <input
                                                            type="text"
                                                            className="wo-input-sm"
                                                            placeholder="Brief description"
                                                            value={item.description}
                                                            onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="wo-form-group">
                                                        <label className="wo-label-sm">Quantity</label>
                                                        <input
                                                            type="number"
                                                            className="wo-input-sm"
                                                            placeholder="1"
                                                            value={item.quantity}
                                                            onChange={(e) => handleLineItemChange(item.id, 'quantity', parseInt(e.target.value) || 0)}
                                                        />
                                                    </div>
                                                    <div className="wo-form-group">
                                                        <label className="wo-label-sm">Unit Price</label>
                                                        <input
                                                            type="number"
                                                            className="wo-input-sm"
                                                            placeholder="0.00"
                                                            value={item.unitPrice}
                                                            onChange={(e) => handleLineItemChange(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                                        />
                                                    </div>
                                                    <div className="wo-form-group">
                                                        <label className="wo-label-sm">Total</label>
                                                        <div className="wo-total-display">
                                                            {fmtCurrency(item.quantity * item.unitPrice)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <button className="wo-add-item-btn" onClick={handleAddLineItem}>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    Add Line Item
                                </button>

                                {lineItems.length > 0 && (
                                    <div className="wo-subtotal-card">
                                        <div className="wo-subtotal-row">
                                            <span>Subtotal</span>
                                            <span className="wo-subtotal-amount">{fmtCurrency(calculateSubtotal())}</span>
                                        </div>
                                    </div>
                                )}
                            </section>

                            {/* Documents Section */}
                            <section className="wo-card wo-card-animated">
                                <div className="wo-card-header">
                                    <div className="wo-icon-wrapper wo-icon-documents">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="wo-card-title">Documents & Attachments</h2>
                                        <p className="wo-card-subtitle">Upload blueprints, reports, and other files</p>
                                    </div>
                                </div>

                                {/* Upload Area */}
                                <div className="wo-upload-area">
                                    <input
                                        type="file"
                                        id="file-upload"
                                        className="wo-file-input"
                                        onChange={handleFileUpload}
                                        disabled={uploadingFile}
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.dwg,.dxf"
                                    />
                                    <label htmlFor="file-upload" className="wo-upload-label">
                                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                            <path d="M24 32V16M16 24L24 16L32 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M38 32V38C38 39.0609 37.5786 40.0783 36.8284 40.8284C36.0783 41.5786 35.0609 42 34 42H14C12.9391 42 11.9217 41.5786 11.1716 40.8284C10.4214 40.0783 10 39.0609 10 38V32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <span className="wo-upload-text">
                                            {uploadingFile ? 'Uploading...' : 'Click to upload or drag and drop'}
                                        </span>
                                        <span className="wo-upload-hint">
                                            PDF, DOC, Images, CAD files (max 10MB)
                                        </span>
                                    </label>
                                </div>

                                {/* Documents List */}
                                {documents.length > 0 && (
                                    <div className="wo-documents-list">
                                        {documents.map((doc, index) => (
                                            <div key={index} className="wo-document-item">
                                                <div className="wo-document-icon">
                                                    {doc.type.includes('pdf') ? (
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M14 2V8H20" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    ) : doc.type.includes('image') ? (
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <rect x="3" y="3" width="18" height="18" rx="2" stroke="#10b981" strokeWidth="2" />
                                                            <circle cx="8.5" cy="8.5" r="1.5" fill="#10b981" />
                                                            <path d="M21 15L16 10L5 21" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    ) : (
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M14 2V8H20" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    )}
                                                </div>
                                                <div className="wo-document-info">
                                                    <div className="wo-document-name">{doc.name}</div>
                                                    <div className="wo-document-meta">
                                                        {formatFileSize(doc.size)} • {new Date(doc.uploadedAt).toLocaleDateString()}
                                                    </div>
                                                </div>
                                                <div className="wo-document-actions">
                                                    <a
                                                        href={buildApiUrl(doc.url)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="wo-document-view-btn"
                                                        title="View document"
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                            <path d="M1 9C1 9 4 3 9 3C14 3 17 9 17 9C17 9 14 15 9 15C4 15 1 9 1 9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                            <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
                                                        </svg>
                                                    </a>
                                                    <button
                                                        className="wo-document-remove-btn"
                                                        onClick={() => handleRemoveDocument(index)}
                                                        title="Remove document"
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                            <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                        </div>

                        {/* Right Column - Schedule */}
                        <div className="wo-right-column">
                            <section className="wo-card wo-card-sticky wo-card-animated">
                                <div className="wo-card-header">
                                    <div className="wo-icon-wrapper wo-icon-schedule">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M16 2V6M8 2V6M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="wo-card-title">Schedule</h2>
                                        <p className="wo-card-subtitle">Set the job date & time</p>
                                    </div>
                                </div>

                                <div className="wo-schedule-toggle">
                                    <button
                                        className={`wo-toggle-btn ${scheduleLater ? 'active' : ''}`}
                                        onClick={() => setScheduleLater(true)}
                                    >
                                        Schedule Later
                                    </button>
                                    <button
                                        className={`wo-toggle-btn ${!scheduleLater ? 'active' : ''}`}
                                        onClick={() => setScheduleLater(false)}
                                    >
                                        Schedule Now
                                    </button>
                                </div>

                                {!scheduleLater && (
                                    <>
                                        {/* Premium Calendar */}
                                        <div className="wo-calendar">
                                            <div className="wo-calendar-header">
                                                <button className="wo-calendar-nav" onClick={previousMonth}>
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </button>
                                                <h3 className="wo-calendar-month">{formatMonthYear(currentMonth)}</h3>
                                                <button className="wo-calendar-nav" onClick={nextMonth}>
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </button>
                                            </div>

                                            <div className="wo-calendar-grid">
                                                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                                                    <div key={i} className="wo-calendar-day-name">{day}</div>
                                                ))}

                                                {Array.from({ length: startingDayOfWeek }).map((_, i) => (
                                                    <div key={`empty-${i}`} className="wo-calendar-day empty"></div>
                                                ))}

                                                {Array.from({ length: daysInMonth }).map((_, i) => {
                                                    const day = i + 1;
                                                    return (
                                                        <button
                                                            key={day}
                                                            className={`wo-calendar-day ${isDateSelected(day) ? 'selected' : ''} ${isInRange(day) ? 'range' : ''} ${isToday(day) ? 'today' : ''}`}
                                                            onClick={() => selectDate(day)}
                                                        >
                                                            {day}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {selectedDate && (
                                            <div className="wo-selected-date">
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M13.3333 2.66667H2.66667C1.93029 2.66667 1.33334 3.26362 1.33334 4V13.3333C1.33334 14.0697 1.93029 14.6667 2.66667 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V4C14.6667 3.26362 14.0697 2.66667 13.3333 2.66667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M10.6667 1.33334V4.00001M5.33334 1.33334V4.00001M1.33334 6.66667H14.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {selectedDate.toLocaleDateString('en-US', {
                                                    weekday: 'long',
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </div>
                                        )}

                                        <div className="wo-time-grid" style={{ marginTop: '18px' }}>
                                            <div className="wo-form-group">
                                                <label className="wo-label">Start Date</label>
                                                <input
                                                    type="date"
                                                    className="wo-input"
                                                    value={selectedDate ? selectedDate.toISOString().slice(0, 10) : ''}
                                                    onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value) : null)}
                                                />
                                            </div>
                                            <div className="wo-form-group">
                                                <label className="wo-label">End Date (optional)</label>
                                                <input
                                                    type="date"
                                                    className="wo-input"
                                                    value={selectedEndDate ? selectedEndDate.toISOString().slice(0, 10) : ''}
                                                    onChange={(e) => setSelectedEndDate(e.target.value ? new Date(e.target.value) : null)}
                                                />
                                            </div>
                                        </div>

                                        {/* Time Selection */}
                                        <div className="wo-time-grid">
                                            <div className="wo-form-group">
                                                <label className="wo-label">Start Time</label>
                                                <input
                                                    type="time"
                                                    className="wo-input"
                                                    value={startTime}
                                                    onChange={(e) => setStartTime(e.target.value)}
                                                />
                                            </div>
                                            <div className="wo-form-group">
                                                <label className="wo-label">End Time</label>
                                                <input
                                                    type="time"
                                                    className="wo-input"
                                                    value={endTime}
                                                    onChange={(e) => setEndTime(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </section>

                            {/* Priority Selection */}
                            <section className="wo-card wo-card-animated">
                                <div className="wo-card-header">
                                    <div className="wo-icon-wrapper wo-icon-priority">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="wo-card-title">Priority Level</h2>
                                        <p className="wo-card-subtitle">Set the urgency of this job</p>
                                    </div>
                                </div>

                                <div className="wo-priority-options">
                                    <button
                                        type="button"
                                        className={`wo-priority-btn wo-priority-low ${priority === 'low' ? 'active' : ''}`}
                                        onClick={() => setPriority('low')}
                                    >
                                        <div className="wo-priority-icon">
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                <path d="M10 14L4 8L5.4 6.6L10 11.2L14.6 6.6L16 8L10 14Z" fill="currentColor" />
                                            </svg>
                                        </div>
                                        <span className="wo-priority-label">Low</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`wo-priority-btn wo-priority-medium ${priority === 'medium' ? 'active' : ''}`}
                                        onClick={() => setPriority('medium')}
                                    >
                                        <div className="wo-priority-icon">
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                <rect x="4" y="9" width="12" height="2" fill="currentColor" />
                                            </svg>
                                        </div>
                                        <span className="wo-priority-label">Medium</span>
                                    </button>

                                    <button
                                        type="button"
                                        className={`wo-priority-btn wo-priority-high ${priority === 'high' ? 'active' : ''}`}
                                        onClick={() => setPriority('high')}
                                    >
                                        <div className="wo-priority-icon">
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                <path d="M10 6L16 12L14.6 13.4L10 8.8L5.4 13.4L4 12L10 6Z" fill="currentColor" />
                                            </svg>
                                        </div>
                                        <span className="wo-priority-label">High</span>
                                    </button>
                                </div>
                            </section>

                            {/* Action Buttons */}
                            <div className="wo-actions">
                                <button className="wo-btn wo-btn-secondary" onClick={() => navigate('/admin/work-orders')}>
                                    Cancel
                                </button>
                                <button className="wo-btn wo-btn-secondary" onClick={() => handleSave('draft')}>
                                    Save Draft
                                </button>
                                <button className="wo-btn wo-btn-primary" onClick={() => handleSave('scheduled')}>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M15.8333 2.5H4.16667C3.24619 2.5 2.5 3.24619 2.5 4.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V4.16667C17.5 3.24619 16.7538 2.5 15.8333 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M14.1667 2.5V7.5H5.83334V2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M5.83334 12.5H14.1667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Publish Job
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
